import React from "react";
import PropTypes from "prop-types";
import {
  Typography,
  TextField,
  Autocomplete,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import organisationType from "../../../data/organisationType.json";
import ImageUploader from "../../../components/Reusables/ImageControls/ImageUploader";
import StepNavigationButtons from "../../../components/Reusables/StepControls/StepNavigationButtons";
import FieldLabel from "./FieldLabel";
import {
  BASIC_TEXT_FIELD_SX,
  AUTOCOMPLETE_MENU_SX,
  AUTOCOMPLETE_INPUT_BASE_SX,
} from "../onboardingStyles";

const OrganisationStep = ({
  orgLogoFile,
  setOrgLogoFile,
  logoPreview,
  setLogoPreview,
  formData3,
  setFormData3,
  errors,
  data,
  sameAsContact,
  setSameAsContact,
  contactPhoneNumber,
  onBack,
  onProceed,
  loading,
}) => (
  <div>
    <Typography
      variant="h6"
      sx={{
        fontSize: { xs: "22px", sm: "26px" },
        fontWeight: 600,
        color: "var(--app-text)",
        mb: 1,
        fontFamily: "Figtree",
      }}
    >
      Tell us about your Organisation
    </Typography>
    <Typography
      variant="body1"
      sx={{
        fontSize: "16px",
        fontWeight: 500,
        color: "var(--app-text-muted)",
        mb: 1,
        fontFamily: "Figtree",
      }}
    >
      The main healthcare provider or company that runs one or more centres
    </Typography>
    <br />
    <div className="row">
      <div className="col-12">
        <div className="form-group">
          <div
            style={{
              position: "relative",
              width: 120,
              height: 120,
              marginBottom: 30,
            }}
          >
            <ImageUploader
              label="Upload Image"
              file={orgLogoFile}
              setFile={setOrgLogoFile}
              preview={logoPreview}
              setPreview={setLogoPreview}
            />
          </div>
        </div>
      </div>

      <div className="col-12">
        <div className="form-group">
          <FieldLabel required>Organisation Name</FieldLabel>

          <TextField
            fullWidth
            placeholder="Enter Organisation Name"
            value={formData3.organisationName}
            onChange={(e) =>
              setFormData3((prev) => ({
                ...prev,
                organisationName: e.target.value,
              }))
            }
            sx={BASIC_TEXT_FIELD_SX}
          />
          {errors.organisationName && (
            <Typography variant="caption" color="error">
              {errors.organisationName}
            </Typography>
          )}
        </div>
      </div>

      <div className="col-12">
        <div className="form-group">
          <FieldLabel required>Organisation Type</FieldLabel>

          <Autocomplete
            options={organisationType}
            value={formData3.organisationType || null}
            onChange={(event, value) =>
              setFormData3((prev) => ({
                ...prev,
                organisationType: value,
              }))
            }
            disablePortal
            freeSolo={false}
            clearOnEscape
            popupIcon={<KeyboardArrowDownIcon />}
            ListboxProps={{
              style: {
                maxHeight: "250px",
                overflowY: "auto",
              },
            }}
            sx={AUTOCOMPLETE_MENU_SX}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select Organisation Type"
                fullWidth
                error={false}
                sx={{
                  ...AUTOCOMPLETE_INPUT_BASE_SX,
                  "& input": {
                    fontSize: "14px",
                    fontWeight: 400,
                    color: "var(--app-text)",
                  },
                }}
              />
            )}
          />

          {errors.organisationType && (
            <small className="text-danger">{errors.organisationType}</small>
          )}
        </div>
      </div>

      {data?.organisationPhone ||
        (data?.phoneNumber && (
          <div className="col-12">
            <div className="form-group">
              <FieldLabel required>Organisation Contact Number</FieldLabel>

              <TextField
                fullWidth
                placeholder="Enter Organisation Contact Number"
                value={formData3.organisationPhone}
                disabled={sameAsContact}
                onChange={(e) =>
                  setFormData3((prev) => ({
                    ...prev,
                    organisationPhone: e.target.value,
                  }))
                }
                // error={Boolean(errors.organisationPhone)}
                sx={BASIC_TEXT_FIELD_SX}
              />

              {errors.organisationPhone && (
                <Typography variant="caption" color="error">
                  {errors.organisationPhone}
                </Typography>
              )}
            </div>

            <FormControlLabel
              control={
                <Checkbox
                  checked={sameAsContact}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setSameAsContact(checked);
                    setFormData3((prev) => ({
                      ...prev,
                      organisationPhone: checked ? contactPhoneNumber : "",
                    }));
                  }}
                  icon={
                    <span
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 3,
                        border: "2px solid var(--app-border)",
                        display: "inline-block",
                        boxSizing: "border-box",
                      }}
                    />
                  }
                  checkedIcon={
                    <span
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 3,
                        backgroundColor: "#F5A623",
                        border: "2px solid #F5A623",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxSizing: "border-box",
                      }}
                    >
                      <CheckIcon sx={{ fontSize: 16, color: "#FFFFFF" }} />
                    </span>
                  }
                />
              }
              label={
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 500,
                    color: "#4A4A4A",
                    fontFamily: "Figtree",
                  }}
                >
                  Same as my contact number
                </Typography>
              }
            />
          </div>
        ))}
      <StepNavigationButtons
        onBack={onBack}
        onProceed={onProceed}
        loading={loading}
      />
    </div>
  </div>
);

OrganisationStep.propTypes = {
  orgLogoFile: PropTypes.object,
  setOrgLogoFile: PropTypes.func.isRequired,
  logoPreview: PropTypes.string,
  setLogoPreview: PropTypes.func.isRequired,
  formData3: PropTypes.object.isRequired,
  setFormData3: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  data: PropTypes.object,
  sameAsContact: PropTypes.bool,
  setSameAsContact: PropTypes.func.isRequired,
  contactPhoneNumber: PropTypes.string,
  onBack: PropTypes.func.isRequired,
  onProceed: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

export default OrganisationStep;
